import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '../ui/button-simple';
import { Upload, Download, AlertCircle, CheckCircle } from 'lucide-react';

/**
 * Shared base component for farmer / processor import-export buttons.
 *
 * Props:
 * - entityName   – "farmer" | "processor"
 * - data         – array of entity objects
 * - userRole     – current user's role (only renders for 'admin')
 * - getMunicipality(item) → string|null
 * - onFileChange(file) → Promise<{ type, message }>  – handles parsing, API call, returns status
 * - onExport(items, filename) → void
 * - onDownloadTemplate() → void
 * - accentColor  – tailwind colour token, e.g. "green" or "amber"
 * - infoText     – descriptive text shown in the info banner
 * - templateButtonIcon – optional icon component for the template button (default Download)
 */
export function BaseImportExportButtons({
  entityName,
  data,
  userRole,
  getMunicipality,
  onFileChange,
  onExport,
  onDownloadTemplate,
  accentColor = 'green',
  infoText,
  templateButtonIcon: TemplateIcon = Download,
}) {
  const fileInputRef = useRef(null);
  const exportMenuRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importStatus, setImportStatus] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [municipalities, setMunicipalities] = useState([]);

  const plural = `${entityName}s`;

  // Extract unique municipalities
  React.useEffect(() => {
    if (data && data.length > 0) {
      const unique = [...new Set(data.map(getMunicipality).filter(Boolean))].sort();
      setMunicipalities(unique);
    }
  }, [data, getMunicipality]);

  // Close export menu on outside click
  React.useEffect(() => {
    const handler = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    };
    if (showExportMenu) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showExportMenu]);

  // Auto-hide status after 5 s
  React.useEffect(() => {
    if (importStatus) {
      const t = setTimeout(() => setImportStatus(null), 5000);
      return () => clearTimeout(t);
    }
  }, [importStatus]);

  if (userRole !== 'admin') return null;

  const handleImportClick = () => fileInputRef.current?.click();

  const handleFileChangeInternal = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';
    setImporting(true);
    setImportStatus(null);
    try {
      const status = await onFileChange(file);
      setImportStatus(status);
    } catch (error) {
      setImportStatus({
        type: 'error',
        message: error.message || `Failed to import ${plural}. Please check the file format.`,
      });
    } finally {
      setImporting(false);
    }
  };

  const handleTemplateDownload = () => {
    try {
      onDownloadTemplate();
      setImportStatus({ type: 'success', message: 'Template downloaded successfully!' });
    } catch (error) {
      setImportStatus({ type: 'error', message: 'Failed to download template. Please try again.' });
    }
  };

  const handleExportClick = (municipality = null) => {
    try {
      let items = data;
      let exportLabel = 'all';

      if (municipality) {
        items = data.filter((d) => getMunicipality(d) === municipality);
        exportLabel = municipality.toLowerCase().replace(/\s+/g, '_');
      }

      if (items.length === 0) {
        setImportStatus({
          type: 'error',
          message: municipality ? `No ${plural} found in ${municipality}` : `No ${plural} to export`,
        });
        setShowExportMenu(false);
        return;
      }

      const timestamp = new Date().toISOString().split('T')[0];
      const filename = municipality
        ? `${plural}_${exportLabel}_${timestamp}.xlsx`
        : `${plural}_export_${timestamp}.xlsx`;

      onExport(items, filename);

      setImportStatus({
        type: 'success',
        message: municipality
          ? `Successfully exported ${items.length} ${entityName}(s) from ${municipality}`
          : `Successfully exported ${items.length} ${entityName}(s)`,
      });
      setShowExportMenu(false);
      setTimeout(() => setImportStatus(null), 3000);
    } catch (error) {
      setImportStatus({ type: 'error', message: `Failed to export ${plural}. Please try again.` });
      setShowExportMenu(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        <Button
          onClick={handleTemplateDownload}
          className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2 transition-all transform hover:scale-105"
        >
          <TemplateIcon className="h-4 w-4" />
          Download Template
        </Button>

        <Button
          onClick={handleImportClick}
          disabled={importing}
          className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2 transition-all transform hover:scale-105"
        >
          <Upload className="h-4 w-4" />
          {importing ? 'Importing...' : 'Import Excel'}
        </Button>

        {/* Export Dropdown */}
        <div className="relative" ref={exportMenuRef}>
          <Button
            onClick={() => setShowExportMenu(!showExportMenu)}
            variant="outline"
            className={`border-${accentColor}-600 text-${accentColor}-600 hover:bg-${accentColor}-50 flex items-center gap-2`}
          >
            <Download className="h-4 w-4" />
            Export Excel
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </Button>

          {showExportMenu && (
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-[100] max-h-96 overflow-y-auto">
              <div className="py-1">
                <button
                  onClick={() => handleExportClick(null)}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center justify-between group"
                >
                  <span className="text-sm font-medium text-gray-700">All Municipalities</span>
                  <span className="text-xs text-gray-500 group-hover:text-gray-700">
                    ({data.length} {plural})
                  </span>
                </button>

                {municipalities.length > 0 && <div className="border-t border-gray-200 my-1" />}

                {municipalities.map((municipality) => {
                  const count = data.filter((d) => getMunicipality(d) === municipality).length;
                  return (
                    <button
                      key={municipality}
                      onClick={() => handleExportClick(municipality)}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center justify-between group"
                    >
                      <span className="text-sm text-gray-700">{municipality}</span>
                      <span className="text-xs text-gray-500 group-hover:text-gray-700">
                        ({count} {plural})
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFileChangeInternal} className="hidden" />
      </div>

      {importing && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <div className="flex items-center gap-2">
            <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
            <span className="text-sm font-medium text-blue-800">
              Importing {plural}... Please wait, this may take a while for large files.
            </span>
          </div>
        </div>
      )}

      {importStatus && (
        <div
          className={`flex items-center gap-2 p-3 rounded-md text-sm ${
            importStatus.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {importStatus.type === 'success' ? (
            <CheckCircle className="h-4 w-4 flex-shrink-0" />
          ) : (
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
          )}
          <span>{importStatus.message}</span>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-blue-600" />
          <span className="text-sm text-blue-800">
            <strong>Comprehensive Import:</strong> {infoText}
          </span>
        </div>
      </div>
    </div>
  );
}

BaseImportExportButtons.propTypes = {
  entityName: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  userRole: PropTypes.string,
  getMunicipality: PropTypes.func,
  onFileChange: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  onDownloadTemplate: PropTypes.func.isRequired,
  accentColor: PropTypes.string,
  infoText: PropTypes.string,
  templateButtonIcon: PropTypes.elementType,
};
